import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2, Card, CardContent } from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import AppSearchDropDown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  FinanceAnalyticsFilters,
  FinanceAnalyticsSelectedFilters,
  parseQueryParamsForFinanceAnalyticsFilters,
} from "../../../../../models/Dashboard/FinanceDashboard/FinanceAnalyticsFilters";
import { Customer } from "../../../../../models/Customer/Customer";
import { formatAmount } from "../../../../../views/components/common/functions/formatDate";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import { JobTypes } from "../../../../../models/Jobs/JobRecord";
import JobRecordsTable from "../../components/tables/JobRecordsTable";

const FINANCE_ANALYTICS_FILTERS_KEY = "FINANCE_ANALYTICS_FILTERS_KEY";

const OpsVendorsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    financeAnalyticsStore,
    orderStore,
    userStore,
    customerStore,
    invoiceStore,
    jobsStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useNotification();
  let apiTimer: any;

  const initialFilters: FinanceAnalyticsFilters =
    parseQueryParamsForFinanceAnalyticsFilters(
      null,
      // localStorage.getItem(FINANCE_ANALYTICS_FILTERS_KEY)
      //   ? JSON.parse(localStorage.getItem(FINANCE_ANALYTICS_FILTERS_KEY)!)
      //   : null,
      searchParams
    );

  const [filters, setFilters] =
    useState<FinanceAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<FinanceAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    fetchPendingJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      FINANCE_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters)
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.order_id) {
      filters.order_id.forEach((order_id) =>
        newFilters.push({ id: "order_id", value: order_id.value })
      );
    }

    if (filters.customer_id) {
      filters.customer_id.forEach((customer_id) =>
        newFilters.push({ id: "customer_id", value: customer_id.value })
      );
    }

    setSelectedFiltersData(newFilters);
  };

  const fetchPendingJobs = async () => {
    try {
      jobsStore.jobs = [];
      await jobsStore.getJobs({
        page_no: 1,
        limit: 2,
        name: JobTypes.DOWNLOAD_INVOICES_BACKGROUND,
        sort_by: "start_time",
        sort_type: "DESC",
      });
    } catch (error) {
      console.error("Error fetching pending jobs", error);
    }
  };

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await financeAnalyticsStore.getAllAnalyticsData({
        order_id: selectedFilters.order_id,
        customer_id: selectedFilters.customer_id,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleDownloadInvoices = async () => {
    setLoading(true);

    try {
      const response = await invoiceStore.downloadInvoicesInBackground(
        selectedFilters.start_date,
        selectedFilters.end_date
      );

      await fetchPendingJobs();

      showSuccess(
        response.message ||
          "Invoice download job started. You will receive an email once the download is complete. Reach out to support if you don't receive the email or want to check the status of the job."
      );
    } catch (error: any) {
      console.error("Error downloading invoices", error);
      showError(
        "Error",
        error?.response?.data?.message || "Failed to download invoices."
      );
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "customer_id":
        setFilters((prev) => ({
          ...prev,
          customer_id:
            filters.customer_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      customer_id: [],
      order_id: [],
    }));

    setSelectedFiltersData([]);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleCustomerSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText.length > 2) {
      apiTimer = setTimeout(() => {
        customerStore
          .getCustomers({
            search_text: searchText,
            limit: 30,
            page_no: 1,
          })
          .catch((err) => {
            showError("Error", err?.response?.data?.message);
          });
      }, 1500);
    }
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_FINANCE_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box sx={{ margin: "8px" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 2,
              }}
            >
              <Grid2
                container
                spacing={2}
                columns={12}
                sx={{ width: "100%", alignItems: "flex-end" }}
              >
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Customer"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Customer"
                    fontSize="0.9rem"
                    value={filters.customer_id || []}
                    list={
                      customerStore.customers?.map((customer: Customer) => ({
                        id: customer.id?.toString() || "",
                        value: customer.name || "",
                      })) || []
                    }
                    local={false}
                    handleSearch={(searchText) =>
                      handleCustomerSearch(searchText!)
                    }
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        customer_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Order ID
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Order Id"
                    value={filters.order_id || []}
                    fontSize="0.9rem"
                    list={orderStore.orderIds.map((item) => {
                      return { id: String(item), value: String(item) };
                    })}
                    local={false}
                    handleSearch={handleOrderIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2
                  size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {selectedFiltersData.length > 0 && (
                  <Box sx={{ paddingTop: "15px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        borderRadius: 2,
                      }}
                    >
                      {selectedFiltersData.map((filter, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors.blackBorder,
                            height: "34px",
                            paddingX: "6px",
                          }}
                        >
                          <CustomTypography
                            variant="heading9"
                            color={theme.palette.customColors.text}
                            style={{ marginRight: "5px" }}
                          >
                            {filter.value}
                          </CustomTypography>
                          <Button
                            onClick={() => handleRemoveFilter(index)}
                            sx={{
                              width: "14px",
                              height: "14px",
                              minWidth: "14px",
                              minHeight: "14px",
                              fontSize: "15px",
                              padding: "0px",
                              border: "1px solid #E2E2E2",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              overflow: "hidden",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid2>
            </Box>
            <Box>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
                style={{ margin: "12px 10px 0px 0px" }}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>
            {jobsStore.jobs.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  marginY: 3,
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <JobRecordsTable
                  data={jobsStore.jobs}
                  totalCount={jobsStore.total_count}
                  onRefreshClick={() => fetchPendingJobs()}
                  onDownloadClick={handleDownloadInvoices}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 2 },
                marginY: 3,
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {financeAnalyticsStore.tats.map((item, index) => (
                <Box
                  key={`${item.label}-${index}`}
                  sx={{
                    flex: { xs: "1 1 140px", sm: "1 1 180px", md: "1 1 200px" },
                    maxWidth: {
                      xs: "calc(50% - 8px)",
                      sm: "calc(33.33% - 16px)",
                      md: "calc(25% - 16px)",
                      lg: "calc(20% - 16px)",
                      xl: "calc(16.66% - 16px)",
                    },
                    minWidth: { xs: "140px", sm: "160px", md: "180px" },
                  }}
                >
                  <TypeFilterCard
                    title={item.label}
                    message={item.info}
                    value={
                      item.label === "Net Purchase Value" ||
                      item.label === "Account Receivable"
                        ? formatAmount(item.value)
                        : item.value ?? "0"
                    }
                    color={""}
                    icon={"https://content.swageazy.com/icons/boxes_green.png"}
                    onClick={(title) => {
                      switch (title) {
                        case "Invoices":
                          const invoice_report_url = `/report/data?title=${encodeURI(
                            "Invoices Report"
                          )}&base_url=/v5/finance-analytics&path=/data/invoice-report&${searchParams.toString()}`;
                          window.open(invoice_report_url, "_blank");
                          break;
                        case "Orders Without Invoice":
                        case "Account Receivable":
                          const orders_without_invoice_url = `/report/data?title=${encodeURI(
                            "Orders Without Invoice"
                          )}&base_url=/v5/finance-analytics&path=/data/orders-without-invoice&${searchParams.toString()}`;
                          window.open(orders_without_invoice_url, "_blank");
                          break;
                        default:
                          break;
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(OpsVendorsDashboard);
